/**
 * Tile-grid spatial partition math for viewport culling (map-FPS perf
 * Stage 3). Pure functions, no GL — `mergedBuffers.ts` uses the AABB/tile
 * assignment at rebuild time, the renderer uses `visibleTiles` per frame.
 *
 * Cross-platform contract (identical constants + rounding on iOS
 * `MergedBufferBuilder` and Android `MergedVboBuilder`; the shared numeric
 * test vectors in `tileGrid.test.ts` are asserted verbatim on all three
 * platforms):
 * - canvas 4096×4096, 8×8 grid of 512-px tiles, `index = tileY * 8 + tileX`;
 * - an element belongs to exactly ONE tile — the tile containing its AABB
 *   center; a center on a tile boundary goes to the higher tile; an
 *   off-canvas center clamps into the grid;
 * - an element whose AABB side is STRICTLY greater than one tile (or whose
 *   AABB is missing — no vertices) goes to the per-type GLOBAL bucket,
 *   drawn whenever any tile is visible. One-tile assignment means no
 *   duplicate draws, so the (disabled) main-pass blend state is moot —
 *   see the blend note in `mergedBuffers.ts`.
 */

/** Canvas edge length in canvas units — matches `CANVAS_SIZE` in viewMatrix.ts. */
const CANVAS_EDGE = 4096
/** Tiles per grid axis. */
const GRID_AXIS = 8
/** Tile edge length (canvas units). */
const TILE_EDGE = CANVAS_EDGE / GRID_AXIS
/** Total tile count (64). */
export const TILE_COUNT = GRID_AXIS * GRID_AXIS
/** Sentinel tile index for the per-type global bucket (oversized / no AABB). */
export const GLOBAL_TILE = -1

/** Axis-aligned bounding box in canvas units. */
export interface Aabb {
  readonly minX: number
  readonly minY: number
  readonly maxX: number
  readonly maxY: number
}

/**
 * AABB over an element's fill + stroke vertices (interleaved 6-float
 * layout, position at offsets 0/1). `null` when the element has no
 * vertices at all — the caller assigns it to the global bucket.
 */
export function aabbOfVertices(fill: Float32Array, stroke: Float32Array): Aabb | null {
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const arr of [fill, stroke]) {
    for (let i = 0; i + 1 < arr.length; i += 6) {
      const x = arr[i]
      const y = arr[i + 1]
      if (x < minX) minX = x
      if (x > maxX) maxX = x
      if (y < minY) minY = y
      if (y > maxY) maxY = y
    }
  }
  if (minX === Infinity) return null
  return { minX, minY, maxX, maxY }
}

/**
 * The single tile an AABB is assigned to, or {@link GLOBAL_TILE} when the
 * AABB is missing or oversized (a side STRICTLY greater than one tile —
 * exactly one tile length still fits). Assignment = the tile containing
 * the AABB center; `floor(center / TILE_EDGE)` puts a boundary center in
 * the higher tile, and the result clamps into the grid for off-canvas
 * centers.
 */
export function tileIndexForAabb(aabb: Aabb | null): number {
  if (aabb === null) return GLOBAL_TILE
  if (aabb.maxX - aabb.minX > TILE_EDGE || aabb.maxY - aabb.minY > TILE_EDGE) return GLOBAL_TILE
  const tileX = clampAxis(Math.floor((aabb.minX + aabb.maxX) / 2 / TILE_EDGE))
  const tileY = clampAxis(Math.floor((aabb.minY + aabb.maxY) / 2 / TILE_EDGE))
  return tileY * GRID_AXIS + tileX
}

/** Camera transform + physical viewport, as the renderer sees them per frame. */
export interface VisibleTilesInput {
  /** Unscaled canvas-space pan (world point at the viewport origin). */
  readonly offsetX: number
  readonly offsetY: number
  readonly zoom: number
  /** Physical viewport size in device pixels (canvas.width / height). */
  readonly viewportW: number
  readonly viewportH: number
}

/**
 * Tile indices intersecting the visible canvas rect
 * `[offset … offset + viewport / zoom]` EXPANDED by a one-tile margin ring
 * in every direction (before the grid clamp), ascending. The margin is
 * load-bearing (contract amendment #2): elements are tiled by AABB CENTER,
 * so an element whose extent reaches into the viewport can live in a tile
 * whose own area is just off-screen — without the ring it would vanish at
 * the viewport edge. Edge tiles are inclusive on BOTH sides (an edge
 * landing exactly on a tile boundary still visits the tile it touches),
 * rows/cols clamp into the grid, and a non-positive zoom or an empty
 * viewport yields no tiles.
 */
export function visibleTiles(input: VisibleTilesInput): number[] {
  const { offsetX, offsetY, zoom, viewportW, viewportH } = input
  if (zoom <= 0 || viewportW <= 0 || viewportH <= 0) return []
  const minTileX = clampAxis(Math.floor(offsetX / TILE_EDGE) - 1)
  const maxTileX = clampAxis(Math.floor((offsetX + viewportW / zoom) / TILE_EDGE) + 1)
  const minTileY = clampAxis(Math.floor(offsetY / TILE_EDGE) - 1)
  const maxTileY = clampAxis(Math.floor((offsetY + viewportH / zoom) / TILE_EDGE) + 1)
  const out: number[] = []
  for (let tileY = minTileY; tileY <= maxTileY; tileY++) {
    for (let tileX = minTileX; tileX <= maxTileX; tileX++) {
      out.push(tileY * GRID_AXIS + tileX)
    }
  }
  return out
}

function clampAxis(tile: number): number {
  return Math.max(0, Math.min(GRID_AXIS - 1, tile))
}
